import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faTimes, faCheck, faEdit, faTrash,
    faRulerHorizontal, faEye, faEyeSlash, faSync,
    faArrowUp, faArrowDown, faChevronDown, faChevronRight,
    faLayerGroup, faTag
} from '@fortawesome/free-solid-svg-icons';
import baseURL from '../../url';
import './TallaManager.css';
import { useTallas } from '../../../hooks/useTallas';

// Componente Toast
const Toast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="talla-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`talla-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function TallaManager({ onTallasUpdated }) {
    // Usar el hook personalizado
    const {
        tallas, tiposTallas, loading,
        cargarTallas, cargarTiposTallas, obtenerTallasPorTipo,
        agregarTalla, actualizarTalla, eliminarTalla,
        agregarTipoTalla, actualizarTipoTalla, eliminarTipoTalla
    } = useTallas();

    const [toasts, setToasts] = useState([]);
    const [gruposExpandidos, setGruposExpandidos] = useState(new Set());

    // Estados de modales
    const [modalTipoOpen, setModalTipoOpen] = useState(false);
    const [modalTallaOpen, setModalTallaOpen] = useState(false);
    const [editingTipo, setEditingTipo] = useState(null);
    const [editingTalla, setEditingTalla] = useState(null);

    // Estados de formularios
    const [formTipo, setFormTipo] = useState({
        nombre: '',
        descripcion: ''
    });

    const [formTalla, setFormTalla] = useState({
        nombre: '',
        descripcion: '',
        orden: 0,
        idTipoTalla: null
    });

    // Helper para mostrar toasts
    const showToast = (message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    // Función para generar código automáticamente a partir del nombre
    const generarCodigo = (nombre) => {
        return nombre
            .toLowerCase()
            .replace(/\s+/g, '_')           // Espacios a guiones bajos
            .replace(/[áàäâ]/g, 'a')       // Acentos de a
            .replace(/[éèëê]/g, 'e')       // Acentos de e
            .replace(/[íìïî]/g, 'i')       // Acentos de i
            .replace(/[óòöô]/g, 'o')       // Acentos de o
            .replace(/[úùüû]/g, 'u')       // Acentos de u
            .replace(/ñ/g, 'n')            // Ñ a n
            .replace(/[^a-z0-9_]/g, '')    // Solo letras, números y guiones bajos
            .replace(/_{2,}/g, '_')        // Múltiples guiones bajos a uno solo
            .replace(/^_+|_+$/g, '');      // Remover guiones del inicio y final
    };

    // Alternar expansión de grupo
    const toggleGrupo = (idTipo) => {
        setGruposExpandidos(prev => {
            const newSet = new Set(prev);
            if (newSet.has(idTipo)) {
                newSet.delete(idTipo);
            } else {
                newSet.add(idTipo);
            }
            return newSet;
        });
    };

    // Expandir todos los grupos al inicio
    useEffect(() => {
        if (tiposTallas.length > 0) {
            setGruposExpandidos(new Set(tiposTallas.map(t => t.idTipoTalla)));
        }
    }, [tiposTallas]);

    // ===== GESTIÓN DE TIPOS =====
    const abrirModalTipo = (tipo = null) => {
        setEditingTipo(tipo);
        setFormTipo({
            nombre: tipo?.nombre || '',
            descripcion: tipo?.descripcion || ''
        });
        setModalTipoOpen(true);
    };

    const cerrarModalTipo = () => {
        setModalTipoOpen(false);
        setEditingTipo(null);
        setFormTipo({ nombre: '', descripcion: '' });
    };

    const guardarTipo = async () => {
        if (!formTipo.nombre.trim()) {
            showToast('El nombre es obligatorio', 'warning');
            return;
        }

        // Generar código automáticamente basado en el nombre
        const codigoGenerado = generarCodigo(formTipo.nombre);

        if (!codigoGenerado) {
            showToast('No se pudo generar un código válido a partir del nombre', 'error');
            return;
        }

        const resultado = editingTipo
            ? await actualizarTipoTalla(editingTipo.idTipoTalla, { ...formTipo, codigo: codigoGenerado })
            : await agregarTipoTalla(codigoGenerado, formTipo.nombre, formTipo.descripcion);

        if (resultado.success) {
            showToast(resultado.mensaje, 'success');
            cerrarModalTipo();
            onTallasUpdated?.();
        } else {
            showToast(resultado.error, 'error');
        }
    };

    const eliminarTipo = async (tipo) => {
        if (!window.confirm(`¿Eliminar el tipo "${tipo.nombre}"? Esto eliminará todas las tallas asociadas.`)) {
            return;
        }

        const resultado = await eliminarTipoTalla(tipo.idTipoTalla);
        if (resultado.success) {
            showToast(resultado.mensaje, 'success');
            onTallasUpdated?.();
        } else {
            showToast(resultado.error, 'error');
        }
    };

    // ===== GESTIÓN DE TALLAS =====
    const abrirModalTalla = (talla = null, idTipoTalla = null) => {
        setEditingTalla(talla);
        setFormTalla({
            nombre: talla?.nombre || '',
            descripcion: talla?.descripcion || '',
            orden: talla?.orden || 0,
            idTipoTalla: talla?.idTipoTalla || idTipoTalla
        });
        setModalTallaOpen(true);
    };

    const cerrarModalTalla = () => {
        setModalTallaOpen(false);
        setEditingTalla(null);
        setFormTalla({ nombre: '', descripcion: '', orden: 0, idTipoTalla: null });
    };

    const guardarTalla = async () => {
        if (!formTalla.nombre.trim()) {
            showToast('El nombre es obligatorio', 'warning');
            return;
        }

        const resultado = editingTalla
            ? await actualizarTalla(editingTalla.idTalla, formTalla)
            : await agregarTalla(formTalla.nombre, formTalla.descripcion, formTalla.orden, formTalla.idTipoTalla);

        if (resultado.success) {
            showToast(resultado.mensaje, 'success');
            cerrarModalTalla();
            onTallasUpdated?.();
        } else {
            showToast(resultado.error, 'error');
        }
    };

    const eliminarTallaItem = async (talla) => {
        if (!window.confirm(`¿Eliminar la talla "${talla.nombre}"?`)) {
            return;
        }

        const resultado = await eliminarTalla(talla.idTalla);
        if (resultado.success) {
            showToast(resultado.mensaje, 'success');
            onTallasUpdated?.();
        } else {
            showToast(resultado.error, 'error');
        }
    };

    // Agrupar tallas por tipo
    const tallasAgrupadas = React.useMemo(() => {
        const grupos = {};

        // Crear un grupo especial para tallas sin tipo
        const tallasSinTipo = tallas.filter(t => !t.idTipoTalla);
        if (tallasSinTipo.length > 0) {
            grupos['sin_tipo'] = {
                tipo: { idTipoTalla: 'sin_tipo', nombre: 'Tallas sin grupo', codigo: 'sin_grupo' },
                tallas: tallasSinTipo.sort((a, b) => a.orden - b.orden)
            };
        }

        // Agrupar tallas por tipo
        tiposTallas.forEach(tipo => {
            const tallasDelTipo = tallas.filter(t => t.idTipoTalla === tipo.idTipoTalla);
            if (tallasDelTipo.length > 0 || tipo.activo) {
                grupos[tipo.idTipoTalla] = {
                    tipo,
                    tallas: tallasDelTipo.sort((a, b) => a.orden - b.orden)
                };
            }
        });

        return grupos;
    }, [tallas, tiposTallas]);

    return (
        <div className="talla-manager">
            <Toast toasts={toasts} />

            {/* Header */}
            <div className="talla-manager-header">
                <div className="header-title">
                    <FontAwesomeIcon icon={faLayerGroup} />
                    <span>Gestión de Grupos de Tallas</span>
                    <div className="talla-count">
                        {tiposTallas.length} grupos • {tallas.length} tallas
                    </div>
                </div>
                <div className="header-actions">
                    <button
                        className="btn-add-grupo"
                        onClick={() => abrirModalTipo()}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Crear Grupo
                    </button>
                </div>
            </div>

            {/* Lista de grupos */}
            <div className="grupos-container">
                {Object.entries(tallasAgrupadas).map(([idGrupo, { tipo, tallas: tallasDelGrupo }]) => {
                    const isExpanded = gruposExpandidos.has(idGrupo);
                    const isSinTipo = idGrupo === 'sin_tipo';

                    return (
                        <div key={idGrupo} className={`grupo-card ${!tipo.activo && !isSinTipo ? 'inactive' : ''}`}>
                            {/* Header del grupo */}
                            <div className="grupo-header">
                                <button
                                    className="grupo-toggle"
                                    onClick={() => toggleGrupo(idGrupo)}
                                >
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronDown : faChevronRight}
                                    />
                                </button>

                                <div className="grupo-info">
                                    <div className="grupo-title">
                                        <FontAwesomeIcon icon={faLayerGroup} />
                                        <span>{tipo.nombre}</span>
                                        <div className="grupo-badge">
                                            {tallasDelGrupo.length} talla{tallasDelGrupo.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                    {tipo.descripcion && (
                                        <div className="grupo-description">{tipo.descripcion}</div>
                                    )}
                                </div>

                                <div className="grupo-actions">
                                    <button
                                        className="btn-add-talla-grupo"
                                        onClick={() => abrirModalTalla(null, isSinTipo ? null : tipo.idTipoTalla)}
                                        title="Agregar talla al grupo"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                    {!isSinTipo && (
                                        <>
                                            <button
                                                className="btn-edit-grupo"
                                                onClick={() => abrirModalTipo(tipo)}
                                                title="Editar grupo"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                className="btn-delete-grupo"
                                                onClick={() => eliminarTipo(tipo)}
                                                title="Eliminar grupo"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Tallas del grupo */}
                            {isExpanded && (
                                <div className="tallas-grid">
                                    {tallasDelGrupo.length > 0 ? (
                                        tallasDelGrupo.map((talla) => (
                                            <div
                                                key={talla.idTalla}
                                                className={`talla-item ${!talla.activo ? 'inactive' : ''}`}
                                            >
                                                <div className="talla-content">
                                                    <div className="talla-badge">
                                                        {talla.nombre}
                                                    </div>
                                                    <div className="talla-info">
                                                       {/*  <div className="talla-name">{talla.nombre}</div> */}
                                                        {talla.descripcion && (
                                                            <div className="talla-description">{talla.descripcion}</div>
                                                        )}
                                                        <div className="talla-order">Orden: {talla.orden}</div>
                                                    </div>
                                                </div>

                                                <div className="talla-actions">
                                                    <button
                                                        className="btn-edit-talla"
                                                        onClick={() => abrirModalTalla(talla)}
                                                        title="Editar talla"
                                                    >
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                    <button
                                                        className="btn-delete-talla"
                                                        onClick={() => eliminarTallaItem(talla)}
                                                        title="Eliminar talla"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-grupo">
                                            <FontAwesomeIcon icon={faTag} size="2x" />
                                            <p>No hay tallas en este grupo</p>
                                            <button
                                                className="btn-add-first-talla"
                                                onClick={() => abrirModalTalla(null, isSinTipo ? null : tipo.idTipoTalla)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} />
                                                Agregar primera talla
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {Object.keys(tallasAgrupadas).length === 0 && (
                    <div className="empty-state">
                        <FontAwesomeIcon icon={faLayerGroup} size="3x" />
                        <h3>No hay grupos de tallas</h3>
                        <p>Crea tu primer grupo para organizar las tallas</p>
                        <button
                            className="btn-add-grupo"
                            onClick={() => abrirModalTipo()}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            Crear Primer Grupo
                        </button>
                    </div>
                )}
            </div>

            {/* Modal para Tipo/Grupo */}
            {modalTipoOpen && (
                <div className="talla-modal-backdrop">
                    <div className="talla-modal">
                        <div className="modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faLayerGroup} />
                                {editingTipo ? 'Editar Grupo' : 'Crear Nuevo Grupo'}
                            </h3>
                            <button className="btn-close" onClick={cerrarModalTipo}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Nombre del Grupo *</label>
                                <input
                                    type="text"
                                    value={formTipo.nombre}
                                    onChange={(e) => setFormTipo(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="ej: Ropa Mujer (XS-XXL)"
                                    maxLength="100"
                                />
                                <small>El código se generará automáticamente a partir del nombre</small>
                            </div>

                            <div className="form-group">
                                <label>Descripción</label>
                                <textarea
                                    value={formTipo.descripcion}
                                    onChange={(e) => setFormTipo(prev => ({ ...prev, descripcion: e.target.value }))}
                                    placeholder="Descripción opcional del grupo de tallas"
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={cerrarModalTipo}>
                                Cancelar
                            </button>
                            <button
                                className="btn-save"
                                onClick={guardarTipo}
                                disabled={loading || !formTipo.nombre.trim()}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                {editingTipo ? 'Actualizar' : 'Crear'} Grupo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para Talla */}
            {modalTallaOpen && (
                <div className="talla-modal-backdrop">
                    <div className="talla-modal">
                        <div className="modal-header">
                            <h3>
                                <FontAwesomeIcon icon={faTag} />
                                {editingTalla ? 'Editar Talla' : 'Agregar Nueva Talla'}
                            </h3>
                            <button className="btn-close" onClick={cerrarModalTalla}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label>Grupo</label>
                                <select
                                    value={formTalla.idTipoTalla || ''}
                                    onChange={(e) => setFormTalla(prev => ({ ...prev, idTipoTalla: e.target.value || null }))}
                                >
                                    <option value="">Sin grupo específico</option>
                                    {tiposTallas.map(tipo => (
                                        <option key={tipo.idTipoTalla} value={tipo.idTipoTalla}>
                                            {tipo.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Nombre de la Talla *</label>
                                <input
                                    type="text"
                                    value={formTalla.nombre}
                                    onChange={(e) => setFormTalla(prev => ({ ...prev, nombre: e.target.value }))}
                                    placeholder="ej: XL, 42, Única"
                                    maxLength="20"
                                />
                            </div>

                            <div className="form-group">
                                <label>Descripción</label>
                                <input
                                    type="text"
                                    value={formTalla.descripcion}
                                    onChange={(e) => setFormTalla(prev => ({ ...prev, descripcion: e.target.value }))}
                                    placeholder="ej: Extra Large, Talla 42"
                                    maxLength="100"
                                />
                            </div>

                            <div className="form-group">
                                <label>Orden</label>
                                <input
                                    type="number"
                                    value={formTalla.orden}
                                    onChange={(e) => setFormTalla(prev => ({ ...prev, orden: parseInt(e.target.value) || 0 }))}
                                    min="0"
                                />
                                <small>Determina el orden de aparición dentro del grupo</small>
                            </div>

                            <div className="talla-preview">
                                <div className="preview-label">Vista Previa:</div>
                                <div className="preview-badge">
                                    {formTalla.nombre || 'Sin nombre'}
                                </div>
                                <div className="preview-info">
                                    <div>{formTalla.nombre || 'Sin nombre'}</div>
                                    {formTalla.descripcion && <div>{formTalla.descripcion}</div>}
                                    <div>Orden: {formTalla.orden}</div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={cerrarModalTalla}>
                                Cancelar
                            </button>
                            <button
                                className="btn-save"
                                onClick={guardarTalla}
                                disabled={loading || !formTalla.nombre.trim()}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                                {editingTalla ? 'Actualizar' : 'Agregar'} Talla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}